/**
 * SEO utility functions for dynamic title and meta tag management
 */

export interface SEOData {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
}

export const updatePageSEO = (seoData: SEOData) => {
  // Update document title
  document.title = seoData.title;

  // Update meta description
  updateMetaTag('name', 'description', seoData.description);
  
  // Update meta keywords if provided
  if (seoData.keywords) {
    updateMetaTag('name', 'keywords', seoData.keywords);
  }

  // Update canonical URL if provided
  if (seoData.canonical) {
    updateCanonicalLink(seoData.canonical);
  }

  // Update Open Graph tags
  if (seoData.ogTitle) {
    updateMetaTag('property', 'og:title', seoData.ogTitle);
  }
  if (seoData.ogDescription) {
    updateMetaTag('property', 'og:description', seoData.ogDescription);
  }
  if (seoData.ogImage) {
    updateMetaTag('property', 'og:image', seoData.ogImage);
  }

  // Update Twitter Card tags
  if (seoData.twitterTitle) {
    updateMetaTag('name', 'twitter:title', seoData.twitterTitle);
  }
  if (seoData.twitterDescription) {
    updateMetaTag('name', 'twitter:description', seoData.twitterDescription);
  }
  if (seoData.twitterImage) {
    updateMetaTag('name', 'twitter:image', seoData.twitterImage);
  }
};

const updateMetaTag = (attribute: string, name: string, content: string) => {
  let metaTag = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement;
  
  if (!metaTag) {
    metaTag = document.createElement('meta');
    metaTag.setAttribute(attribute, name);
    document.head.appendChild(metaTag);
  }
  
  metaTag.content = content;
};

const updateCanonicalLink = (href: string) => {
  let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
  
  if (!canonicalLink) {
    canonicalLink = document.createElement('link');
    canonicalLink.rel = 'canonical';
    document.head.appendChild(canonicalLink);
  }
  
  canonicalLink.href = href;
};

// SEO data for each page/route
export const SEO_CONFIG: Record<string, SEOData> = {
  'landing': {
    title: 'Snip Rush - Create Viral Shorts in Minutes | Free Video Editor for YouTube, TikTok & Instagram',
    description: 'Free browser-based video editor for YouTube Shorts, TikTok, and Instagram Reels. Add captions, edit videos, create compositions with transitions. 100% private - all processing happens in your browser.',
    keywords: 'YouTube Shorts editor, TikTok video editor, Instagram Reels creator, free video editor, add captions, video composition, shorts creator, vertical video editor, 9:16 editor',
    canonical: 'https://sniprush.com/',
    ogTitle: 'Snip Rush - Create Viral Shorts in Minutes',
    ogDescription: 'Free browser-based video editor for YouTube Shorts, TikTok, and Instagram Reels. Add captions, edit videos, create compositions. 100% private.',
    ogImage: 'https://sniprush.com/logo.png',
    twitterTitle: 'Snip Rush - Create Viral Shorts in Minutes',
    twitterDescription: 'Free browser-based video editor for YouTube Shorts, TikTok, and Instagram Reels. 100% private - all processing in your browser.',
    twitterImage: 'https://sniprush.com/logo.png'
  },
  'how-it-works': {
    title: 'How Snip Rush Works - Free Video Editor for Shorts | Step-by-Step Guide',
    description: 'Learn how Snip Rush works - the free browser-based video editor for creating YouTube Shorts, TikTok videos, and Instagram Reels. Upload, edit, process, and download in minutes.',
    keywords: 'how video editor works, browser video editing, YouTube Shorts tutorial, TikTok video creation, Instagram Reels guide, free video editor tutorial',
    canonical: 'https://sniprush.com/about',
    ogTitle: 'How Snip Rush Works - Free Video Editor Guide',
    ogDescription: 'Learn how to create viral shorts with Snip Rush. Step-by-step guide for YouTube Shorts, TikTok, and Instagram Reels.',
    ogImage: 'https://sniprush.com/logo.png',
    twitterTitle: 'How Snip Rush Works - Free Video Editor Guide',
    twitterDescription: 'Learn how to create viral shorts with Snip Rush. Step-by-step guide for YouTube Shorts, TikTok, and Instagram Reels.',
    twitterImage: 'https://sniprush.com/logo.png'
  },
  'tutorials': {
    title: 'Video Editing Tutorials - Master Shorts Creation | Snip Rush Learning Center',
    description: 'Master video editing with our comprehensive tutorials. Learn caption creation, video editing basics, advanced composition, thumbnail design, and shorts clipping techniques.',
    keywords: 'video editing tutorials, YouTube Shorts tutorial, TikTok video tutorial, Instagram Reels tutorial, caption creation guide, video editing basics, thumbnail design tutorial',
    canonical: 'https://sniprush.com/tutorials',
    ogTitle: 'Video Editing Tutorials - Master Shorts Creation',
    ogDescription: 'Master video editing with comprehensive tutorials for YouTube Shorts, TikTok, and Instagram Reels. Learn caption creation, editing basics, and more.',
    ogImage: 'https://sniprush.com/logo.png',
    twitterTitle: 'Video Editing Tutorials - Master Shorts Creation',
    twitterDescription: 'Master video editing with comprehensive tutorials for YouTube Shorts, TikTok, and Instagram Reels.',
    twitterImage: 'https://sniprush.com/logo.png'
  },
  'privacy-policy': {
    title: 'Privacy Policy - 100% Private Video Editing | Snip Rush',
    description: 'Snip Rush Privacy Policy - Learn how we protect your privacy. All video processing happens locally in your browser. Your files never leave your device.',
    keywords: 'privacy policy, private video editing, browser-based processing, data protection, video editor privacy, local processing',
    canonical: 'https://sniprush.com/privacy',
    ogTitle: 'Privacy Policy - 100% Private Video Editing',
    ogDescription: 'Learn how Snip Rush protects your privacy. All processing happens locally in your browser - your files never leave your device.',
    ogImage: 'https://sniprush.com/logo.png',
    twitterTitle: 'Privacy Policy - 100% Private Video Editing',
    twitterDescription: 'Learn how Snip Rush protects your privacy. All processing happens locally in your browser.',
    twitterImage: 'https://sniprush.com/logo.png'
  },
  'contact-us': {
    title: 'Contact Us - Get Help with Snip Rush Video Editor | Support & Feedback',
    description: 'Contact Snip Rush support team. Get help with video editing, report bugs, request features, or provide feedback. We respond within 24 hours.',
    keywords: 'contact support, video editor help, bug report, feature request, Snip Rush support, video editing help',
    canonical: 'https://sniprush.com/contact',
    ogTitle: 'Contact Us - Get Help with Snip Rush',
    ogDescription: 'Get help with Snip Rush video editor. Contact support, report bugs, request features. We respond within 24 hours.',
    ogImage: 'https://sniprush.com/logo.png',
    twitterTitle: 'Contact Us - Get Help with Snip Rush',
    twitterDescription: 'Get help with Snip Rush video editor. Contact support, report bugs, request features.',
    twitterImage: 'https://sniprush.com/logo.png'
  },
  'terms-and-conditions': {
    title: 'Terms & Conditions - Snip Rush Video Editor | Free Service Agreement',
    description: 'Snip Rush Terms & Conditions - Free video editing service agreement. Learn about usage rights, privacy protection, and service limitations.',
    keywords: 'terms and conditions, service agreement, free video editor terms, usage rights, video editing terms',
    canonical: 'https://sniprush.com/terms',
    ogTitle: 'Terms & Conditions - Snip Rush Video Editor',
    ogDescription: 'Snip Rush Terms & Conditions - Free video editing service agreement. Learn about usage rights and privacy protection.',
    ogImage: 'https://sniprush.com/logo.png',
    twitterTitle: 'Terms & Conditions - Snip Rush Video Editor',
    twitterDescription: 'Snip Rush Terms & Conditions - Free video editing service agreement.',
    twitterImage: 'https://sniprush.com/logo.png'
  },
  'auto-captions': {
    title: 'Caption Creator - Add Professional Captions to Videos | Snip Rush',
    description: 'Create professional captions for YouTube Shorts, TikTok, and Instagram Reels. AI transcription, manual editing, custom styling. Boost engagement by 40-80%.',
    keywords: 'caption creator, video captions, YouTube Shorts captions, TikTok captions, Instagram Reels captions, AI transcription, video subtitles',
    canonical: 'https://sniprush.com/auto-captions',
    ogTitle: 'Caption Creator - Add Professional Captions',
    ogDescription: 'Create professional captions for YouTube Shorts, TikTok, and Instagram Reels. AI transcription and custom styling.',
    ogImage: 'https://sniprush.com/logo.png',
    twitterTitle: 'Caption Creator - Add Professional Captions',
    twitterDescription: 'Create professional captions for YouTube Shorts, TikTok, and Instagram Reels.',
    twitterImage: 'https://sniprush.com/logo.png'
  },
  'video-editor': {
    title: 'Video Editor - Trim, Cut & Edit Videos | Snip Rush Free Editor',
    description: 'Professional video editor for YouTube Shorts, TikTok, and Instagram Reels. Trim, cut, add overlays, extract audio. Timeline-based editing with effects.',
    keywords: 'video editor, video trimming, video cutting, YouTube Shorts editor, TikTok editor, Instagram Reels editor, timeline editing, video effects',
    canonical: 'https://sniprush.com/video-editor',
    ogTitle: 'Video Editor - Trim, Cut & Edit Videos',
    ogDescription: 'Professional video editor for YouTube Shorts, TikTok, and Instagram Reels. Trim, cut, add overlays with timeline editing.',
    ogImage: 'https://sniprush.com/logo.png',
    twitterTitle: 'Video Editor - Trim, Cut & Edit Videos',
    twitterDescription: 'Professional video editor for YouTube Shorts, TikTok, and Instagram Reels.',
    twitterImage: 'https://sniprush.com/logo.png'
  },
  'video-composer-v3': {
    title: 'Video Composer - Multi-Track Timeline Editor | Snip Rush',
    description: 'Advanced video composer with timeline-based workflows, multi-track audio, overlays, transitions, and fast browser rendering. Build complete vertical videos end-to-end.',
    keywords: 'video composer, timeline editor, multi-track timeline, advanced video editor, video transitions, vertical video editor, browser video editor',
    canonical: 'https://sniprush.com/video-composer',
    ogTitle: 'Video Composer by Snip Rush',
    ogDescription: 'Design complete vertical videos with multi-track editing, overlays, audio mixing, and pro transitions — all in your browser.',
    ogImage: 'https://sniprush.com/logo.png',
    twitterTitle: 'Video Composer',
    twitterDescription: 'Create complete shorts with multi-track editing, overlays, audio mixing, and transitions directly in your browser.',
    twitterImage: 'https://sniprush.com/logo.png'
  },
  'video-stack-composer': {
    title: 'Video Stack Composer - Simple Layer Editor for Shorts | Snip Rush',
    description: 'Simple stack-based composer for YouTube Shorts, TikTok, and Instagram Reels. Add text, emojis, images, and shapes on a vertical canvas.',
    keywords: 'simple video editor, video stack composer, layer editor, add text to video, emojis on video, vertical video editor',
    canonical: 'https://sniprush.com/video-stack-composer',
    ogTitle: 'Video Stack Composer - Simple Layer Editor',
    ogDescription: 'Compose vertical videos with text, emojis, images, and shapes on a simple layer stack.',
    ogImage: 'https://sniprush.com/logo.png',
    twitterTitle: 'Video Stack Composer - Simple Layer Editor',
    twitterDescription: 'Compose vertical videos with text, emojis, images, and shapes.',
    twitterImage: 'https://sniprush.com/logo.png'
  },
  'shorts-clipper': {
    title: 'Shorts Clipper - Extract Multiple Clips from Long Videos | Snip Rush',
    description: 'Turn long videos into multiple Shorts clips. Auto-detect highlights, crop to vertical format, batch export. Perfect for YouTube Shorts, TikTok, and Instagram Reels.',
    keywords: 'shorts clipper, video clipping, extract clips, YouTube Shorts clips, TikTok clips, Instagram Reels clips, vertical video, batch processing',
    canonical: 'https://sniprush.com/shorts-clipper',
    ogTitle: 'Shorts Clipper - Extract Multiple Clips',
    ogDescription: 'Turn long videos into multiple Shorts clips. Auto-detect highlights, crop to vertical format, batch export.',
    ogImage: 'https://sniprush.com/logo.png',
    twitterTitle: 'Shorts Clipper - Extract Multiple Clips',
    twitterDescription: 'Turn long videos into multiple Shorts clips. Auto-detect highlights and crop to vertical format.',
    twitterImage: 'https://sniprush.com/logo.png'
  },
  'thumbnail': {
    title: 'Thumbnail Creator - Design Eye-Catching Thumbnails | Snip Rush',
    description: 'Create professional thumbnails for YouTube Shorts. Custom backgrounds, text, images, and SVG shapes. Design thumbnails that increase click-through rates.',
    keywords: 'thumbnail creator, YouTube thumbnail, thumbnail design, video thumbnail, click-through rate, thumbnail maker, YouTube Shorts thumbnail',
    canonical: 'https://sniprush.com/thumbnail',
    ogTitle: 'Thumbnail Creator - Design Eye-Catching Thumbnails',
    ogDescription: 'Create professional thumbnails for YouTube Shorts. Custom backgrounds, text, images, and SVG shapes.',
    ogImage: 'https://sniprush.com/logo.png',
    twitterTitle: 'Thumbnail Creator - Design Eye-Catching Thumbnails',
    twitterDescription: 'Create professional thumbnails for YouTube Shorts with custom backgrounds and text.',
    twitterImage: 'https://sniprush.com/logo.png'
  },
  'banner': {
    title: 'YouTube Banner Creator - Design Channel Banners | Snip Rush',
    description: 'Create professional YouTube channel banners with perfect 2048×1152 dimensions. Add text, images, and shapes for channel branding.',
    keywords: 'YouTube banner creator, channel banner, YouTube channel art, banner design, channel branding, YouTube banner maker',
    canonical: 'https://sniprush.com/banner',
    ogTitle: 'YouTube Banner Creator - Design Channel Banners',
    ogDescription: 'Create professional YouTube channel banners with perfect dimensions. Add text, images, and shapes for branding.',
    ogImage: 'https://sniprush.com/logo.png',
    twitterTitle: 'YouTube Banner Creator - Design Channel Banners',
    twitterDescription: 'Create professional YouTube channel banners with perfect dimensions.',
    twitterImage: 'https://sniprush.com/logo.png'
  },
  'image-to-video': {
    title: 'Image to Video Creator - Convert Images to Videos | Snip Rush',
    description: 'Convert images to videos for YouTube Shorts, TikTok, and Instagram Reels. Add transitions, text overlays, and create engaging video content from static images.',
    keywords: 'image to video, convert images to video, slideshow creator, photo to video, image slideshow, video from images',
    canonical: 'https://sniprush.com/image-to-video',
    ogTitle: 'Image to Video Creator - Convert Images to Videos',
    ogDescription: 'Convert images to videos for YouTube Shorts, TikTok, and Instagram Reels. Add transitions and text overlays.',
    ogImage: 'https://sniprush.com/logo.png',
    twitterTitle: 'Image to Video Creator - Convert Images to Videos',
    twitterDescription: 'Convert images to videos for YouTube Shorts, TikTok, and Instagram Reels.',
    twitterImage: 'https://sniprush.com/logo.png'
  },
  'gif-creator': {
    title: 'GIF Creator - Convert Videos to Optimized GIFs | Snip Rush',
    description: 'Convert videos to optimized GIFs with frame control and platform optimization. Create GIFs for social media with perfect quality and file size.',
    keywords: 'GIF creator, video to GIF, GIF converter, animated GIF, social media GIF, GIF optimization',
    canonical: 'https://sniprush.com/gif-creator',
    ogTitle: 'GIF Creator - Convert Videos to Optimized GIFs',
    ogDescription: 'Convert videos to optimized GIFs with frame control and platform optimization for social media.',
    ogImage: 'https://sniprush.com/logo.png',
    twitterTitle: 'GIF Creator - Convert Videos to Optimized GIFs',
    twitterDescription: 'Convert videos to optimized GIFs with frame control and platform optimization.',
    twitterImage: 'https://sniprush.com/logo.png'
  },
  'sitemap': {
    title: 'Sitemap - All Snip Rush Pages | Video Editor Tools & Resources',
    description: 'Complete sitemap of Snip Rush video editor tools and resources. Find all pages, tutorials, and features for creating YouTube Shorts, TikTok, and Instagram Reels.',
    keywords: 'sitemap, video editor pages, Snip Rush tools, YouTube Shorts tools, TikTok tools, Instagram Reels tools',
    canonical: 'https://sniprush.com/sitemap',
    ogTitle: 'Sitemap - All Snip Rush Pages',
    ogDescription: 'Complete sitemap of Snip Rush video editor tools and resources for creating viral shorts.',
    ogImage: 'https://sniprush.com/logo.png',
    twitterTitle: 'Sitemap - All Snip Rush Pages',
    twitterDescription: 'Complete sitemap of Snip Rush video editor tools and resources.',
    twitterImage: 'https://sniprush.com/logo.png'
  },
  'blog': {
    title: 'Blog & Tutorials - Video Editing Tips & Guides | Snip Rush',
    description: 'Learn video editing with our comprehensive blog. Tutorials, tips, and guides for creating viral YouTube Shorts, TikTok videos, and Instagram Reels with Snip Rush.',
    keywords: 'video editing blog, YouTube Shorts tutorial, TikTok tips, Instagram Reels guide, video editing tips, shorts creation guide, viral content tips',
    canonical: 'https://sniprush.com/blog',
    ogTitle: 'Blog & Tutorials - Video Editing Tips & Guides',
    ogDescription: 'Learn video editing with comprehensive tutorials and tips for creating viral shorts on YouTube, TikTok, and Instagram.',
    ogImage: 'https://sniprush.com/logo.png',
    twitterTitle: 'Blog & Tutorials - Video Editing Tips & Guides',
    twitterDescription: 'Learn video editing with comprehensive tutorials and tips for creating viral shorts.',
    twitterImage: 'https://sniprush.com/logo.png'
  },
  'blog-post': {
    title: 'Blog Post - Video Editing Tips & Tutorials | Snip Rush',
    description: 'Read our latest blog post about video editing, YouTube Shorts creation, and tips for viral content. Learn from our comprehensive guides and tutorials.',
    keywords: 'video editing blog post, YouTube Shorts tutorial, TikTok tips, Instagram Reels guide, video editing tips',
    canonical: 'https://sniprush.com/blog',
    ogTitle: 'Blog Post - Video Editing Tips & Tutorials',
    ogDescription: 'Read our latest blog post about video editing and creating viral shorts.',
    ogImage: 'https://sniprush.com/logo.png',
    twitterTitle: 'Blog Post - Video Editing Tips & Tutorials',
    twitterDescription: 'Read our latest blog post about video editing and creating viral shorts.',
    twitterImage: 'https://sniprush.com/logo.png'
  }
};

// Function to get dynamic SEO data for blog posts
export const getBlogPostSEO = (post: any) => {
  return {
    title: `${post.title} | Snip Rush Blog`,
    description: post.excerpt,
    keywords: post.tags.join(', ') + ', video editing, YouTube Shorts, TikTok, Instagram Reels',
    canonical: `https://sniprush.com/blog/${post.slug}`,
    ogTitle: post.title,
    ogDescription: post.excerpt,
    ogImage: 'https://sniprush.com/logo.png',
    twitterTitle: post.title,
    twitterDescription: post.excerpt,
    twitterImage: 'https://sniprush.com/logo.png'
  };
};
